import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AlertTriangle, Lock } from 'lucide-react';
import { useSession } from '@/core/session';
import { supabase } from '@/core/supabase';
import { Card, Button } from '@/components/ui';
import { FullScreenLoader } from '@/components/Loader';
import { useT } from '@/core/i18n';
import { Sentence } from '@/components/Sentence';

export function RequireAuth({ children }: { children: ReactNode }) {
  const { session, loading, profile, platform, profileError, accountState } = useSession();
  const location = useLocation();

  if (loading) return <BootScreen />;

  if (!session) {
    // Remember where they were headed so signing in lands them there.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  /* A platform operator who has not opened a company belongs in the console.
     They have no profile by design, so without this they would land on the
     "not linked to a company" screen — which is true of them and is not a
     fault, and would be the only thing they ever saw. */
  if (platform && !platform.actingCompanyId) {
    return <Navigate to="/platform" replace />;
  }

  /* A database fault is worth a screen of its own — a refused grant, a schema
     that is not readable. Those are things somebody has to go and fix, and
     they are nothing to do with the person looking at them. */
  if (!profile && profileError) return <SetupIncomplete message={profileError} />;

  /* A company that has been switched off, or an account that has. Both used to
     land on the application form — which invited a suspended company's
     accountant to register the company they already have, on the other side of
     the screen they cannot see. */
  if (!profile && accountState === 'suspended') return <SwitchedOff what="company" />;
  if (!profile && accountState === 'disabled') return <SwitchedOff what="account" />;

  /* No profile, no fault and nothing switched off means this account belongs to
     no company yet, which is what somebody applying to join looks like. */
  if (!profile) return <Navigate to="/apply" replace />;

  return <>{children}</>;
}

/**
 * A door that is shut, and which one.
 *
 * Deliberately says nothing about why. The person reading this cannot act on
 * the reason and the reason is frequently commercial — an unpaid invoice, a
 * dispute, an investigation. What they need is who to ask, which is their own
 * administrator for an account and NoorOne for a company.
 */
function SwitchedOff({ what }: { what: 'company' | 'account' }) {
  const t = useT();
  return (
    <div className="min-h-dvh grid place-items-center bg-bg p-6">
      <Card className="max-w-md text-center">
        <span className="w-12 h-12 rounded-full bg-surface-2 text-muted grid place-items-center mx-auto">
          <Lock size={22} />
        </span>
        <h1 className="text-[18px] font-bold tracking-tight mt-4">
          {what === 'company' ? t('off.companyTitle') : t('off.accountTitle')}
        </h1>
        <p className="text-[13.5px] text-muted mt-2 leading-relaxed">
          {what === 'company' ? t('off.companyBody') : t('off.accountBody')}
        </p>
        <Button
          variant="secondary"
          size="sm"
          className="mt-5"
          onClick={() => void supabase.auth.signOut()}
        >
          {t('ra.signOut')}
        </Button>
      </Card>
    </div>
  );
}

/**
 * Somebody with an account and no company.
 *
 * Which is everybody applying, and also anybody whose account exists but was
 * never linked — the two are indistinguishable from a session, and the
 * application screen tells them apart by whether there is an application. What
 * it must not do is let a person who *does* have a company sit on it: they have
 * a workspace, and this page would be a dead end with no way back.
 */
export function RequireApplicant({ children }: { children: ReactNode }) {
  const { session, loading, profile, platform } = useSession();
  const location = useLocation();

  if (loading) return <BootScreen />;
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (platform) return <Navigate to="/platform" replace />;
  if (profile) return <Navigate to="/" replace />;

  return <>{children}</>;
}

/**
 * The console's own guard.
 *
 * Deliberately not a permission check against `can()`. A company's super admin
 * holds `*` and that grant is about their company; it says nothing at all about
 * the platform, and a guard that confused the two would hand every customer's
 * administrator a list of every other customer.
 */
export function RequirePlatform({ children }: { children: ReactNode }) {
  const { session, loading, platform } = useSession();
  const location = useLocation();

  if (loading) return <BootScreen />;
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!platform) return <Navigate to="/" replace />;

  return <>{children}</>;
}

function BootScreen() {
  return <FullScreenLoader message="Loading your workspace…" />;
}

function SetupIncomplete({ message }: { message: string | null }) {
  const t = useT();
  return (
    <div className="min-h-dvh grid place-items-center bg-bg p-6">
      <Card className="max-w-lg">
        <div className="flex items-start gap-3.5">
          <span className="shrink-0 w-10 h-10 rounded-card bg-warn-subtle text-warn grid place-items-center">
            <AlertTriangle size={19} />
          </span>
          <div className="min-w-0">
            <h1 className="text-[17px] font-bold tracking-tight">
              {t('ra.noWorkspace')}
            </h1>
            <p className="text-[13px] text-muted mt-2 leading-relaxed">
              {message ??
                'Your account exists in Supabase Auth but is not linked to a company.'}
            </p>

            <div className="mt-4 p-3 rounded-card bg-surface-2">
              <p className="text-[11.5px] font-semibold text-muted uppercase tracking-[0.1em]">
                {t('ra.toFix')}
              </p>
              <ol className="text-[12.5px] text-muted mt-2 flex flex-col gap-1.5 list-decimal ps-4">
                <li>
                  <Sentence
                    k="ra.runMigration"
                    parts={{
                      file: (
                        <code className="font-mono text-[11.5px]">
                          supabase/migrations/0013_schema_grants.sql
                        </code>
                      ),
                    }}
                  />
                </li>
                <li>
                  <Sentence
                    k="ra.runBootstrap"
                    parts={{
                      file: (
                        <code className="font-mono text-[11.5px]">
                          supabase/setup/bootstrap.sql
                        </code>
                      ),
                    }}
                  />
                </li>
                <li>{t('ra.signOutAndBack')}</li>
              </ol>
            </div>

            <div className="flex gap-2 mt-5">
              <Button size="sm" onClick={() => window.location.reload()}>
                {t('ra.tryAgain')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void supabase.auth.signOut()}
              >
                {t('ra.signOut')}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
